import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    inject
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { AccountingPlanService } from "@app/shared/services/accounting-plan.service";
import { ContactService } from "@app/shared/services/contact.service";
import { DatePeriodService } from "@app/shared/services/date-period.service";
import { DeviceService } from "@app/shared/services/device.service";
import { LocalStoreService } from "@app/shared/services/local-store.service";
import {
    EventChangeComparationParams,
    EventChangePeriodParams
} from "@app/views/auth/report/components/report-toolbar/report-toolbar.component";
import { DesktopComponent } from "@app/views/auth/report/dashboard/desktop/desktop.component";
import { MobileComponent } from "@app/views/auth/report/dashboard/mobile/mobile.component";
import { DateUtils } from "@arch/_shared/utils/date.utils";
import { ReportPreferenceService } from "@arch/domain/application/services";
import { EventsService } from "@arch/domain/application/services/events.service";
import { ThemeService } from "@arch/domain/application/services/theme.service";
import {
    CompanyEntity,
    ReportEntity,
    ScenarioEntity
} from "@arch/domain/entity";
import {
    GetAccountingPlanByNameController,
    GetAvailableController,
    GetCompanyController,
    GetReportController,
    GetScenariosController,
    PopulateReportController,
    UpdateVizByReportController
} from "@arch/presentation/controllers";
import _ from "lodash";

@Component({
    selector: "app-report-dashboard",
    imports: [
        SharedDsnModule,
        SharedMaterialModule,
        DesktopComponent,
        MobileComponent
    ],
    templateUrl: "./report-dashboard.component.html",
    styleUrl: "./report-dashboard.component.scss"
})
export class ReportDashboardComponent implements OnInit {
    @Input() accordionState: string = null;
    @Input() public previewMode: boolean = false;
    @Output() public handleReport = new EventEmitter<EventEmitterReportType>();

    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly deviceService = inject(DeviceService);
    private readonly getReportController = inject(GetReportController);
    private readonly getCompanyController = inject(GetCompanyController);
    private readonly populateReportController = inject(
        PopulateReportController
    );
    private readonly getScenariosController = inject(GetScenariosController);
    private readonly getAvailableController = inject(GetAvailableController);
    private readonly updateVizByReportController = inject(
        UpdateVizByReportController
    );
    private readonly getAccountingPlanByNameController = inject(
        GetAccountingPlanByNameController
    );
    private readonly datePeriodService = inject(DatePeriodService);
    private readonly ls = inject(LocalStoreService);
    private readonly eventsService = inject(EventsService);
    private readonly themeService = inject(ThemeService);
    private readonly contactService = inject(ContactService);
    private readonly preferenceService = inject(ReportPreferenceService);
    private readonly accountingPlanService = inject(AccountingPlanService);

    public report: ReportEntity;
    public company: CompanyEntity;
    public scenarios: ScenarioEntity[];
    public loading: boolean;
    public isMobile = this.deviceService.getIsMobile();

    async ngOnInit(): Promise<void> {
        this.loading = true;
        this.handleReport.emit({
            loading: true,
            company: null,
            report: null
        });
        const { companyId, reportId } = this.route.snapshot.queryParams;
        if (!companyId || !reportId) {
            this.loading = false;
            this.router.navigate(["/"]);
            return;
        }
        const report = await this.getReportController.execute({
            reportId: reportId
        });
        await this.themeService.setDashboardTheme(
            report?.props?.theme || "default"
        );
        const [company, scenarios] = await Promise.all([
            this.getCompanyController.execute({
                companyId: companyId,
                options: {
                    isGroup: report.props.isGroup
                }
            }),
            this.getScenariosController.execute({
                companyId,
                options: {
                    addOriginal: true,
                    addProjection: report.props.isGroup ? false : true
                }
            })
        ]);
        const accountingPlan =
            await this.getAccountingPlanByNameController.execute(
                company.props.accounting?.contract
            );
        if (accountingPlan) {
            this.accountingPlanService.init({ accountingPlan });
        }
        this.ls.setItem("company", company.props);
        if (!this.previewMode) {
            this.updateVizByReportController.execute({
                _id: report.props._id,
                contact: this.contactService.get()
            });
            this.eventsService.send(
                `Dash.${report.type}.${report.humanSourceName}.visualizar`
            );
        } else {
            this.eventsService.send(
                `Dash.${report.type}.${report.humanSourceName}.preview`
            );
        }
        report.setScenarioOptions(scenarios);
        this.report = report;
        this.preferenceService.setAll(report.props?.optionsContent);
        company.setAnalysisPlusStatus(
            this.accountingPlanService.features["AnalysisPlus"]
        );
        this.company = company;
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: this.report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true
            }
        });
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.handleReport.emit({
            loading: false,
            report: this.report,
            company: company
        });
        this.loading = false;
    }

    public async onChangePeriod(event: EventChangePeriodParams): Promise<void> {
        this.loading = true;
        const _report = _.cloneDeep(this.report);
        _report.changeMainPeriod({
            period: event.periodName,
            startDate: event.startDate,
            scenario: event.scenario,
            endDate: event.endDate
        });
        _report.changeMainComparation({
            period: event.periodName,
            date: DateUtils.getPreviousMonthByPeriod(
                event.endDate,
                event.periodName
            ),
            scenario: event.scenario
        });
        _report.setOptionsSources();
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: _report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true
            }
        });
        this.report.changeMainPeriod({
            period: event.periodName,
            startDate: event.startDate,
            scenario: event.scenario,
            endDate: event.endDate
        });
        this.report.changeMainComparation({
            period: event.periodName,
            date: DateUtils.getPreviousMonthByPeriod(
                event.endDate,
                event.periodName
            ),
            scenario: event.scenario
        });
        this.report.setOptionsSources();
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.loading = false;
    }

    public async onChangeComparation(
        event: EventChangeComparationParams
    ): Promise<void> {
        this.loading = true;
        const _report = _.cloneDeep(this.report);
        _report.activateComparation({
            scenario: event.scenario,
            period: event.periodName,
            baseDate: event.dateBase,
            comparationDate: event.comparationDate,
            allMonths: event.allMonthsCompare
        });
        _report.setOptionsSources();
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: _report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true
            }
        });
        this.report.activateComparation({
            scenario: event.scenario,
            period: event.periodName,
            baseDate: event.dateBase,
            comparationDate: event.comparationDate,
            allMonths: event.allMonthsCompare
        });
        this.report.setOptionsSources();
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.loading = false;
    }

    public async onResetPeriod(): Promise<void> {
        this.loading = true;
        const _report = _.cloneDeep(this.report);
        _report.clearComparation();
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: _report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true
            }
        });
        this.report.clearComparation();
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.loading = false;
    }

    public async onChangeBudget(
        budget: ScenarioEntity,
        goals: boolean = this.report.options.goals
    ): Promise<void> {
        this.loading = true;
        const _report = _.cloneDeep(this.report);
        _report.activateBudget(budget);
        if (goals) {
            _report.activateGoals();
        } else {
            _report.deactivateGoals();
        }
        _report.setOptionsSources();
        this.setReportMostRecentDate(_report);
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: _report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true
            }
        });
        this.report.activateBudget(budget);
        if (goals) {
            this.report.activateGoals();
        } else {
            this.report.deactivateGoals();
        }
        this.report.setOptionsSources();
        this.setReportMostRecentDate(this.report);
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.loading = false;
    }

    public async onResetBudget(): Promise<void> {
        this.loading = true;
        const _report = _.cloneDeep(this.report);
        _report.resetBudget();
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: _report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true
            }
        });
        this.report.resetBudget();
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.loading = false;
    }

    public async onChangeAnalysis(analysis: {
        projection: ScenarioEntity;
        budget: ScenarioEntity;
        goals: boolean;
    }): Promise<void> {
        if (analysis.budget) {
            this.onChangeBudget(analysis.budget, analysis.goals);
        } else if (analysis.projection) {
            this.onChangeProjection(analysis.projection, analysis.goals);
        } else if (analysis.goals) {
            this.onChangeGoals();
        } else {
            this.onResetAnalysis();
        }
        if (analysis.budget === null) {
            this.report.resetBudget();
        } else if (analysis.projection === null) {
            this.report.resetProjection();
        } else if (analysis.goals === false) {
            this.report.deactivateGoals();
        }
    }

    public async onResetAnalysis(): Promise<void> {
        this.loading = true;
        const _report = _.cloneDeep(this.report);
        _report.resetBudget();
        _report.resetProjection();
        _report.deactivateGoals();
        this.setReportMostRecentDate(_report);
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: _report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true
            }
        });
        this.report.resetBudget();
        this.report.resetProjection();
        this.report.deactivateGoals();
        this.setReportMostRecentDate(this.report);
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.loading = false;
    }

    private setReportMostRecentDate(report: ReportEntity): void {
        const available = this.getAvailableController.execute({
            companyId: this.company.props._id,
            source: report.options.source,
            sources: report.options.sources,
            scenarioId: report.options.scenario.props._id,
            options: {
                budget: report.options.budget
            }
        });
        let mostRecentDate = this.datePeriodService.getMostRecentDateByPeriod(
            available,
            report.options.period
        );
        if (report.options.projection === "PROGRESSAO_LINEAR") {
            mostRecentDate = DateUtils.addToDate(mostRecentDate, 7, "months");
        }
        report.changeMainPeriod({
            startDate: DateUtils.getPreviousMonth(mostRecentDate, 12),
            endDate: mostRecentDate,
            period: report.options.period,
            scenario: report.options.scenario
        });
        report.changeMainComparation({
            date: DateUtils.getPreviousMonthByPeriod(
                report.options.endDate,
                report.options.period
            ),
            period: report.options.period,
            scenario: report.options.scenario
        });
    }

    public async onChangeProjection(
        projection: ScenarioEntity,
        goals: boolean = this.report.options.goals
    ): Promise<void> {
        this.loading = true;
        const _report = _.cloneDeep(this.report);
        _report.activateProjection(projection);
        if (goals) {
            _report.activateGoals();
        } else {
            _report.deactivateGoals();
        }
        _report.setOptionsSources();
        this.setReportMostRecentDate(_report);
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: _report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true
            }
        });
        this.report.activateProjection(projection);
        if (goals) {
            this.report.activateGoals();
        } else {
            this.report.deactivateGoals();
        }
        this.report.setOptionsSources();
        this.setReportMostRecentDate(this.report);
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.loading = false;
    }

    public async onResetProjection(): Promise<void> {
        this.loading = true;
        const _report = _.cloneDeep(this.report);
        _report.resetProjection();
        this.setReportMostRecentDate(_report);
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: _report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true
            }
        });
        this.report.resetProjection();
        this.setReportMostRecentDate(this.report);
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.loading = false;
    }

    public async onChangeGoals(): Promise<void> {
        this.eventsService.send(
            `Dash.${this.report.type}.${this.report.humanSourceName}.objetivo`
        );
        this.loading = true;
        const _report = _.cloneDeep(this.report);
        _report.activateGoals();
        _report.setOptionsSources();
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: _report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true
            }
        });
        this.report.setOptionsSources();
        this.report.activateGoals();
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.loading = false;
    }

    public async onResetGoals(): Promise<void> {
        this.loading = true;
        const _report = _.cloneDeep(this.report);
        _report.deactivateGoals();
        _report.setOptionsSources();
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: _report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true
            }
        });
        this.report.setOptionsSources();
        this.report.deactivateGoals();
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.loading = false;
    }

    public async onChangeVisualizationsVisible(): Promise<void> {
        this.loading = true;
        await this.populateReportController.execute({
            company: this.company,
            report: this.report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true,
                clearRepositories: true
            }
        });
        this.loading = false;
    }
}

type EventEmitterReportType = {
    loading: boolean;
    report: ReportEntity;
    company?: CompanyEntity;
};
